import React, { memo, useEffect, useState } from 'react';
import clsx from 'clsx';

import ContentSlideActions from '../content-slide-actions/ContentSlideActions';
import { ACTIV8_CONTENT_STATUS, MEDIA_TYPES } from '../../../constants';
import Activ8Image from '../content-media/image/Activ8Image';
import Activ8Video from '../content-media/video/Activ8Video';
import AiGeneratedChip from '../ai-generated-chip/AiGeneratedChip';
import { MEDIA_LINK_VARIANTS, resolveActiv8MediaLink } from '../../utils/resolveActiv8MediaLink';

import scss from './ContentSlide.module.scss';

function ContentSlide({
  slide,
  onPreview,
  onLike,
  onDownload,
  onDownloadSource,
  onStudio,
  onStory,
  onCopyLink,
  onEditTags,
  onDelete,
  onRestore,
  className,
  imageClassName,
  videoClassName,
  popperZIndex,
  disableMoreActionsPortal,
  mediaLinkVariant = MEDIA_LINK_VARIANTS.DISPLAY,
  aiGeneratedSize = 'md',
}) {
  const [isError, setIsError] = useState(false);
  const { converted } = slide;
  const isExpired = slide?.status === ACTIV8_CONTENT_STATUS.EXPIRED;
  const effectiveVariant =
    isExpired && slide.mediaType === MEDIA_TYPES.IMAGE ? MEDIA_LINK_VARIANTS.THUMBNAIL : mediaLinkVariant;
  const { src, poster, backgroundSrc } = resolveActiv8MediaLink(slide, effectiveVariant);
  const lazy = effectiveVariant === MEDIA_LINK_VARIANTS.THUMBNAIL;

  useEffect(() => {
    setIsError(false);
  }, [slide.id, src, poster]);

  const handleMediaError = () => {
    setIsError(true);
  };

  const mappedActionsByType = {
    [MEDIA_TYPES.IMAGE]: {
      onPreview: onPreview,
      onLike: onLike,
      onDownload: onDownload,
      onStudio: onStudio,
      onStory: onStory,
      onCopyLink: onCopyLink,
      onEditTags: onEditTags,
      onDelete: onDelete,
      onRestore: onRestore,
      ...(converted ? { onDownloadSource: onDownloadSource } : {}),
    },
    [MEDIA_TYPES.VIDEO]: {
      onPreview: onPreview,
      onLike: onLike,
      onDownload: onDownload,
      onStudio: onStudio,
      onStory: onStory,
      onCopyLink: onCopyLink,
      onEditTags: onEditTags,
      onDelete: onDelete,
      onRestore: onRestore,
    },
  };

  const slideActions = isExpired ? {} : mappedActionsByType[slide.mediaType];

  return (
    <div className={clsx(scss.slide, className, isExpired && scss.expired)}>
      {slide.mediaType === MEDIA_TYPES.IMAGE && (
        <Activ8Image
          image={slide}
          src={src}
          backgroundSrc={backgroundSrc}
          onError={handleMediaError}
          onPreview={isExpired ? undefined : onPreview}
          imageClassName={imageClassName}
          isExpired={isExpired}
          lazy={lazy}
        />
      )}
      {slide.mediaType === MEDIA_TYPES.VIDEO && (
        <Activ8Video
          src={src}
          poster={poster}
          onError={handleMediaError}
          videoClassName={videoClassName}
          isExpired={isExpired}
          lazy={lazy}
        />
      )}
      {slide.createdInStudio && <AiGeneratedChip size={aiGeneratedSize} />}
      <ContentSlideActions
        slide={slide}
        isError={isError}
        {...slideActions}
        popperZIndex={popperZIndex}
        disableMoreActionsPortal={disableMoreActionsPortal}
      />
    </div>
  );
}

export default memo(ContentSlide, (prevProps, nextProps) => {
  return (
    prevProps.slide?.id === nextProps.slide?.id &&
    prevProps.slide?.mediaType === nextProps.slide?.mediaType &&
    prevProps.slide?.reaction === nextProps.slide?.reaction &&
    prevProps.slide?.status === nextProps.slide?.status &&
    prevProps.slide?.createdInStudio === nextProps.slide?.createdInStudio &&
    prevProps.mediaLinkVariant === nextProps.mediaLinkVariant &&
    prevProps.aiGeneratedSize === nextProps.aiGeneratedSize
  );
});
